<template>
    <form
        v-if="status.detail"
        @submit.prevent="orientationModuleAssignmentUUID ? requestUpdate() : requestCreate()"
    >
        <b-form-group
            label-cols-sm="4"
            label-align="left"
        >
            <template #label>
                <div class="d-flex align-items-center">
                    <span>{{ $t('module.module') }}</span>
                </div>
            </template>
            <multiselect
                v-model="createForm.header"
                deselect-label="Can't remove this value"
                track-by="value"
                label="text"
                :placeholder="$t('message.pleaseSelectX', [$t('module.module')])"
                :options="orientationModuleHeaderDdl"
                :searchable="true"
                :allow-empty="false"
                :append-to-body="true"
                :class="[submitted && !$v.createForm.header.required ? 'border border-danger' : '']"
            />
            <div v-if="submitted && !$v.createForm.header.required" class="invalid-feedback d-block text-right">
                {{ $t("message.requiredX", [$t("module.header")]) }}
            </div>
        </b-form-group>
        <b-form-group
            :label="$t('label.startDate')"
            label-cols-sm="4"
            label-align="left"
        >
            <DatePicker
                v-model="createForm.startDate"
                :config="{ format: 'YYYY-MM-DD', useCurrent: false }"
                class="w-100 cursor-pointer"
                autocomplete="off"
                placeholder="Select date"
            />
        </b-form-group>
        <b-form-group
            :label="$t('label.endDate')"
            label-cols-sm="4"
            label-align="left"
        >
            <DatePicker
                v-model="createForm.endDate"
                :config="{ format: 'YYYY-MM-DD', useCurrent: false }"
                class="w-100 cursor-pointer"
                autocomplete="off"
                placeholder="Select date"
            />
        </b-form-group>
        <b-form-group
            v-if="!orientationModuleAssignmentUUID"
            :label="$t('module.user')"
            label-cols-sm="4"
            label-align="left"
        >
            <div class="row">
                <div class="col pr-1">
                    <div class="position-relative">
                        <b-form-input
                            v-model="userSearchParameter"
                            type="text"
                            class="autocomplete-input"
                            @input="userOptions = []"
                            :placeholder="$t('label.search')"
                            autocomplete="off"
                            @keydown.enter.prevent="userEmailSearch"
                        />
                        <ul
                            v-if="userOptions.length > 0"
                            class="autocomplete-dropdown"
                        >
                            <li
                                v-for="(item, index) in userOptions"
                                :key="index"
                                class="autocomplete-option"
                                @click="selectUser(item)"
                            >
                                {{ item.text }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col-auto pl-1">
                    <b-button
                        type="button"
                        class="w-100"
                        variant="info"
                        v-b-tooltip.hover
                        :title="$t('label.search')"
                        @click="userEmailSearch()"
                    >
                        <i class="mdi mdi-magnify"></i>
                    </b-button>
                </div>
            </div>

        </b-form-group>
        <b-form-group
            v-if="!orientationModuleAssignmentUUID && userSelected.length > 0"
            :label="$t('label.selectedX', [$t('module.user')])"
            label-cols-sm="4"
            label-align="left"
        >
            <ul class="list-unstyled mb-0">
                <li
                    v-for="item in userSelected"
                    :key="item.value"
                    class="d-flex justify-content-between align-items-center p-2 border rounded mb-1"
                >
                    <span class="text-break mr-2">{{ item.text }}</span>
                    <b-button type="button" size="sm" variant="danger" class="flex-shrink-0" @click="removeUser(item)">
                        <i class="mdi mdi-close"></i>
                    </b-button>
                </li>
            </ul>
        </b-form-group>
        <b-form-group
            v-if="!orientationModuleAssignmentUUID"
            :label="$t('module.workgroup')"
            label-cols-sm="4"
            label-align="left"
        >
            <div class="row">
                <div class="col pr-1">
                    <div class="position-relative">
                        <b-form-input
                            v-model="workgroupSearchParameter"
                            type="text"
                            class="autocomplete-input"
                            @input="workgroupOptions = []"
                            :placeholder="$t('label.search')"
                            autocomplete="off"
                            @keydown.enter.prevent="workgroupSearch"
                        />
                        <ul
                            v-if="workgroupOptions.length > 0"
                            class="autocomplete-dropdown"
                        >
                            <li
                                v-for="(item, index) in workgroupOptions"
                                :key="index"
                                class="autocomplete-option"
                                @click="selectWorkgroup(item)"
                            >
                                {{ item.text }}
                            </li>
                        </ul>
                    </div>
                </div>
                <div class="col-auto pl-1">
                    <b-button
                        type="button"
                        class="w-100"
                        variant="info"
                        v-b-tooltip.hover
                        :title="$t('label.search')"
                        @click="workgroupSearch()"
                    >
                        <i class="mdi mdi-magnify"></i>
                    </b-button>
                </div>
            </div>

        </b-form-group>
        <b-form-group
            v-if="!orientationModuleAssignmentUUID && workgroupSelected.length > 0"
            :label="$t('label.selectedX', [$t('module.workgroup')])"
            label-cols-sm="4"
            label-align="left"
        >
            <ul class="list-unstyled mb-0">
                <li
                    v-for="item in workgroupSelected"
                    :key="item.value"
                    class="d-flex justify-content-between align-items-center p-2 border rounded mb-1"
                >
                    <span class="text-break mr-2">{{ item.text }}</span>
                    <b-button type="button" size="sm" variant="danger" class="flex-shrink-0" @click="removeWorkgroup(item)">
                        <i class="mdi mdi-close"></i>
                    </b-button>
                </li>
            </ul>
        </b-form-group>
        <hr class="my-3 mx-n4">
        <div class="d-flex justify-content-end w-100">
            <b-button
                type="button"
                variant="danger"
                class="mr-2 col-5 col-sm-2"
                @click="$emit('done')"
            >{{ $t('label.cancel') }}</b-button>
            <b-button
                type="submit"
                variant="info"
                class="col-5 col-sm-2"
                :disabled="!status.save"
            >
                <span v-if="status.save">{{ orientationModuleAssignmentUUID ? $t('label.updateX', []) : $t('label.createX', []) }}</span>
                <b-spinner v-else small />
            </b-button>
        </div>
    </form>
    <div v-else class="d-flex justify-content-center align-items-center m-5 p-5">
        <b-spinner class="m-5" variant="info" role="status"></b-spinner>
    </div>
</template>
<script>
import { mapState } from "vuex";
import { ORIENTATION_MODULE_ASSIGNMENT_MODULE } from '@/state/api/orientationModuleAssignment.state';
import { lookupMethods, orientationModuleAssignmentMethods } from "@/state/api";
import { required } from "vuelidate/lib/validators";
import Multiselect from "vue-multiselect";
import DatePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';

export default {
    props: {
        orientationModuleAssignmentUUID: {
            type: String,
            required: false,
        },
        searchCriteria: {
            type: Object,
            required: false,
        },
    },
    validations() {
        return {
            createForm: {
                header: { required },
            },
        };
    },
    components: { Multiselect, DatePicker },
    async created() {
        await this.lookupOrientationModuleHeader();
        await this.lookupWorkgroup({ usageType: 2 });
        await this.getOrientationModuleAssignmentData();
    },
    data() {
        return {
            submitted: false,
            userSearchParameter: "",
            userOptions: [],
            userSelected: [],
            workgroupSearchParameter: "",
            workgroupOptions: [],
            workgroupSelected: [],
            createForm: this.defaultForm(),
        };
    },
    methods: {
        ...lookupMethods,
        ...orientationModuleAssignmentMethods,
        defaultForm() {
            return {
                uuid: "",
                header: null,
                startDate: "",
                endDate: "",
                userUuids: [],
                workgroupUuids: [],
            };
        },
        async getOrientationModuleAssignmentData() {
            if (this.orientationModuleAssignmentUUID) {
                await this.getOrientationModuleAssignment({uuid: this.orientationModuleAssignmentUUID})
                    .then((result) => {
                        if (result?.success) {
                            this.createForm = {
                                uuid: this.data.uuid,
                                header: this.data.header ? {
                                    value: this.data.header.uuid,
                                    text: this.data.header.name,
                                } : null,
                                startDate: this.data.startDate,
                                endDate: this.data.endDate,
                                userUuids: [],
                                workgroupUuids: [],
                            };
                        }
                    });
            }
        },
        requestCreate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    headerUuid: this.createForm.header.value,
                    userUuids: this.userSelected.map(user => user.value),
                    workgroupUuids: this.workgroupSelected.map(workgroup => workgroup.value),
                };
                this.createOrientationModuleAssignment({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        requestUpdate() {
            this.submitted = true;
            this.$v.$touch();
            if (this.$v.$invalid) {
                return;
            } else {
                let data = {
                    ...this.createForm,
                    headerUuid: this.createForm.header.value,
                    userUuids: this.userSelected.map(user => user.value),
                    workgroupUuids: this.workgroupSelected.map(workgroup => workgroup.value),
                };
                this.updateOrientationModuleAssignment({data, filter: this.searchCriteria})
                    .then((result) => this.$nextTick(() => {
                        if (result?.success) {
                            this.resetForm();
                            this.$emit("done");
                        }
                    }));
            }
        },
        resetForm() {
            this.createForm = this.defaultForm();
            this.submitted = false;
            this.userSearchParameter = "";
            this.userOptions = [];
            this.userSelected = [];
            this.workgroupSearchParameter = "";
            this.workgroupOptions = [];
            this.workgroupSelected = [];
            this.$v.$reset();
        },
        async selectUser(user) {
            const exists = this.userSelected.some(u => u.value === user.value);
            if (!exists) {
                this.userSelected.push(user);
                this.createForm.userUuids = this.userSelected.map(user => user.value);
            }
            this.userSearchParameter = "";
            this.userOptions = [];
        },
        removeUser(user) {
            this.userSelected = this.userSelected.filter(
                u => u.value !== user.value
            );
            this.createForm.userUuids = this.userSelected.map(user => user.value);
        },
        async userEmailSearch() {
            if (this.userSearchParameter) {
                await this.lookupUserEmail([this.userSearchParameter.trim()])
                    .then((result) => {
                        if (result?.success) {
                            this.userOptions = this.userEmailDdl.filter(item => !this.userSelected.some(user => user.value == item.value));
                        }
                    });
            }
            else {
                this.userOptions = [];
            }
        },
        selectWorkgroup(workgroup) {
            const exists = this.workgroupSelected.some(item => item.value === workgroup.value);
            if (!exists) {
                this.workgroupSelected.push(workgroup);
                this.createForm.workgroupUuids = this.workgroupSelected.map(workgroup => workgroup.value);
            }
            this.workgroupSearchParameter = "";
            this.workgroupOptions = [];
        },
        removeWorkgroup(workgroup) {
            this.workgroupSelected = this.workgroupSelected.filter(
                item => item.value !== workgroup.value
            );
            this.createForm.workgroupUuids = this.workgroupSelected.map(workgroup => workgroup.value);
        },
        workgroupSearch() {
            if (this.workgroupSearchParameter) {
                const search = this.workgroupSearchParameter.toLowerCase();
                this.workgroupOptions = this.workgroupDdl.filter(item => item.text.toLowerCase().includes(search) && !this.workgroupSelected.some(workgroup => workgroup.value == item.value));
            }
            else {
                this.workgroupOptions = [];
            }
        },
    },
    computed: {
        data() {
            return this.$store.state.api.orientationModuleAssignment.details;
        },
        ...mapState(ORIENTATION_MODULE_ASSIGNMENT_MODULE, ['status']),
        orientationModuleHeaderDdl() {
            return this.$store.state.api.lookup.orientationModuleHeaderDdl || [];
        },
        userEmailDdl() {
            return this.$store.state.api.lookup.userEmailDdl || [];
        },
        workgroupDdl() {
            return this.$store.state.api.lookup.workgroupDdl || [];
        },
    },
}
</script>
<style scoped>
.autocomplete-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    z-index: 1050;

    max-height: 200px;
    overflow-y: auto;

    background: white;
    border: 1px solid #ccc;
    border-radius: 0 0 6px 6px;

    list-style: none;
    padding: 0;
    margin: 0;

    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.autocomplete-option {
    padding: 10px;
    cursor: pointer;
    border-bottom: 1px solid #eee;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.autocomplete-option:hover {
    background-color: #f5f5f5;
}

.autocomplete-option:last-child {
    border-bottom: none;
}
@media (max-width: 575.98px) {
    .autocomplete-option {
        white-space: normal;
        overflow-wrap: anywhere;
    }
}
</style>